import React from 'react';
import { Box, IconButton, SxProps } from '@mui/material';
import { Tooltip } from '@ui/Tooltip';
import UndoIcon from '@client/assets/images/undo-icon.svg';
import {
  useUndo,
  useRedo,
  useCanUndo,
  useCanRedo,
  useUndoRedoHotkeysActive,
} from '@client/hooks/use-undo-redo.hooks';

const isMac = navigator.userAgent.includes('Mac');

const UNDO_TOOLTIP = isMac ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)';
const REDO_TOOLTIP = isMac ? 'Redo (⌘⇧Z)' : 'Redo (Ctrl+Shift+Z)';

const UndoRedoButtons: React.FC = () => {
  const undo = useUndo();
  const redo = useRedo();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const hotkeysActive = useUndoRedoHotkeysActive();

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        mx: '4px',
      }}
    >
      <Tooltip title={UNDO_TOOLTIP} placement="bottom" showWhenDisabled>
        <IconButton
          disabled={!hotkeysActive || !canUndo}
          onClick={undo}
          disableRipple
          aria-label="Undo"
          sx={iconButtonSx}
        >
          <Box component="img" src={UndoIcon} alt="" sx={iconSx} />
        </IconButton>
      </Tooltip>
      <Tooltip title={REDO_TOOLTIP} placement="bottom" showWhenDisabled>
        <IconButton
          disabled={!hotkeysActive || !canRedo}
          onClick={redo}
          disableRipple
          aria-label="Redo"
          sx={iconButtonSx}
        >
          <Box
            component="img"
            src={UndoIcon}
            alt=""
            sx={{ ...iconSx, transform: 'scaleX(-1)' }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const iconButtonSx: SxProps = {
  padding: 0,
  '&.Mui-disabled': {
    opacity: 1,
  },
  '&.Mui-disabled img': {
    opacity: 0.25,
  },
};

// Figma: icon bounding box is 12×12px but the SVG path occupies the inner
// ~8×7.5px area (16.67% inset on all sides). Render at the visual path size.
const iconSx: SxProps = {
  display: 'block',
  width: 8,
  height: 'auto',
  opacity: 0.7,
  userSelect: 'none',
  flexShrink: 0,
};

export default UndoRedoButtons;
